import React, { createContext, useState, useEffect } from 'react';
import api from '../api/api';

// Create the auth context
const AuthContext = createContext();

// Create a provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [center, setCenter] = useState(null);

  // Check if user is logged in on component mount
  useEffect(() => {
    const checkLoggedIn = async () => {
      try {
        console.log('AuthContext: Checking if user is logged in');
        
        // Check for token in localStorage
        const token = localStorage.getItem('token');
        if (!token) {
          console.log('AuthContext: No token found, user is not logged in');
          setUser(null);
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }
        
        console.log('AuthContext: Token found, getting user data');
        
        // Get user data from localStorage
        const userData = JSON.parse(localStorage.getItem('user'));
        if (userData) {
          console.log('AuthContext: User found in storage:', userData.email, 'Role:', userData.role);
          
          // IMPORTANT: Try to get stored center data BEFORE setting auth state
          // This ensures center data is available for branding header on first render
          let centerData = null;
          try {
            const storedCenterData = localStorage.getItem('center');
            if (storedCenterData) {
              const parsedCenterData = JSON.parse(storedCenterData);
              console.log('AuthContext: Center data found in storage:', parsedCenterData.name);
              setCenter(parsedCenterData);
              centerData = parsedCenterData;
            } else if (userData.center) {
              // If center data not in localStorage but center ID exists in user data, fetch it
              console.log('AuthContext: Center data not in storage, fetching from API...');
              try {
                const centerResponse = await api.getCenter(userData.center);
                if (centerResponse.data) {
                  centerData = centerResponse.data;
                  localStorage.setItem('center', JSON.stringify(centerData));
                  console.log('AuthContext: Center data fetched and stored:', centerData.name);
                  setCenter(centerData);
                }
              } catch (fetchErr) {
                console.log('AuthContext: Error fetching center data from API:', fetchErr);
              }
            }
          } catch (centerErr) {
            console.log('AuthContext: Error fetching center data from storage:', centerErr);
          }
          
          // Now set auth state after center data is processed
          setUser(userData);
          setIsAuthenticated(true);
        } else {
          console.log('AuthContext: Token exists but no user found, clearing token');
          localStorage.removeItem('token');
          setIsAuthenticated(false);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('AuthContext: Error checking authentication:', err);
        // If there's an error, clear any potentially corrupt data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setIsAuthenticated(false);
        setLoading(false);
      }
    };
    
    checkLoggedIn();
  }, []);

  // Login user
  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      
      // Make login request
      const response = await api.login(email, password);
      
      // Store token in localStorage
      localStorage.setItem('token', response.data.token);
      
      // Prepare user data
      const userData = {
        id: response.data.user.id || '',
        name: response.data.user.name || 'User',
        email: response.data.user.email || email,
        role: response.data.user.role || 'student',
        center: response.data.user.center || '1'
      };
      
      // Store user data
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Log what's being stored
      console.log('Storing user data in localStorage:', userData);
      
      // IMPORTANT: Get center data BEFORE setting user state
      // This ensures header has center data on first render after login
      let centerData = null;
      if (userData.center) {
        try {
          const centerResponse = await api.getCenter(userData.center);
          if (centerResponse.data) {
            // Store center data in localStorage
            centerData = centerResponse.data;
            localStorage.setItem('center', JSON.stringify(centerData));
            console.log('Storing center data in localStorage:', centerData.name);
            
            // Set center state here so it's available immediately
            setCenter(centerData);
          }
        } catch (centerError) {
          console.log('Error fetching center data during login:', centerError);
          // Non-critical error, continue with login
        }
      }
      
      // Set auth state AFTER center data is loaded
      setUser(userData);
      setIsAuthenticated(true);
      console.log('Authentication state updated successfully');

      return true;
    } 
    catch (error) {
      console.log('Login error occurred:', error);
      
      // Extract the meaningful error message from the API response
      let errorMessage = 'Login failed. Please try again.'; // Default fallback message
      
      if (error.response) {
        // The server responded with an error status
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message; // Use the server's error message
        } else if (error.response.status === 400) {
          errorMessage = 'Invalid email or password';
        } else if (error.response.status === 401) {
          errorMessage = 'Unauthorized access';
        } else if (error.response.status === 404) {
          errorMessage = 'Service not found';
        } else if (error.response.status >= 500) {
          errorMessage = 'Server error. Please try again later';
        }
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'No response from server. Please check your connection';
      }
      
      // Set the user-friendly error message
      setError(errorMessage);
      
      return false;
    } 
    finally {
      setLoading(false);
    }
  };

  // Register user
  const register = async (userData) => {
    try {
      console.log('AuthContext: Registration attempt started');
      setError(null);
      setLoading(true);
      
      const response = await api.register(userData);
      console.log('AuthContext: Registration successful');
      
      setLoading(false);
      return { success: true };
    } catch (err) {
      console.log('AuthContext: Registration failed:', err);
      const errorMessage = err.response?.data?.message || 'Registration failed';
      setError(errorMessage);
      setLoading(false);
      return { success: false, error: errorMessage };
    }
  };

  // Logout user
  const logout = async () => {
    try {
      console.log('AuthContext: Logout initiated');
      setLoading(true);

      // Clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('center');
      console.log('AuthContext: Local storage cleared');

      // Reset state
      setUser(null);
      setCenter(null);
      setError(null);
      setIsAuthenticated(false);

      console.log('AuthContext: Logout completed - user state reset');
      setLoading(false);
      return { success: true };
    } catch (err) {
      console.error('AuthContext: Logout error:', err);

      // Still try to clear storage and reset state even if there was an error
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('center');
        console.log('AuthContext: Local storage cleared after error');
      } catch (storageErr) {
        console.error('AuthContext: Failed to clear storage:', storageErr);
      }

      // Always reset user state on logout attempt, even if there were errors
      setUser(null);
      setCenter(null);
      setIsAuthenticated(false);
      setLoading(false);

      return { success: false, error: err.message || 'Logout failed' };
    }
  };

  // Force logout - clears all browser data and reloads the page
  const forceLogout = () => {
    try {
      console.log('AuthContext: Force logout initiated');

      // Clear all localStorage
      localStorage.clear();
      console.log('AuthContext: All local storage cleared');

      // Clear sessionStorage
      sessionStorage.clear();
      console.log('AuthContext: Session storage cleared');

      // Reset state
      setUser(null);
      setCenter(null);
      setError(null);
      setIsAuthenticated(false);

      // Reload the page to ensure clean state
      window.location.href = '/login';

      return { success: true };
    } catch (err) {
      console.error('AuthContext: Force logout error:', err);
      return { success: false, error: err.message };
    }
  };

  // Forgot password
  const forgotPassword = async (email) => {
    try {
      console.log('AuthContext: Password reset initiated');
      setError(null);
      setLoading(true);
      
      const response = await api.forgotPassword(email);
      console.log('AuthContext: Password reset request sent');
      
      setLoading(false);
      return { success: true, message: response.data.message };
    } catch (err) {
      console.log('AuthContext: Password reset failed:', err);
      const errorMessage = err.response?.data?.message || 'Failed to process password reset';
      setError(errorMessage);
      setLoading(false);
      return { success: false, error: errorMessage };
    }
  };

  // Update user data in context and localStorage
  const updateUser = (updatedUserData) => {
    try {
      // Update state
      setUser(updatedUserData);
      
      // Update localStorage
      localStorage.setItem('user', JSON.stringify(updatedUserData));
      
      console.log('AuthContext: User data updated successfully');
      return true;
    } catch (err) {
      console.error('AuthContext: Error updating user data:', err);
      return false;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login,
        register,
        logout,
        forceLogout,
        forgotPassword,
        updateUser,
        isAuthenticated,
        center
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;